import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useStaffAuth } from "@/context/StaffAuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { getErrorDetail } from "@/lib/api";
import { ArrowLeft, Briefcase } from "lucide-react";

export default function StaffLoginPage() {
  const { lang } = useLang();
  const { login } = useStaffAuth();
  const navigate = useNavigate();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const s = await login(identifier.trim(), password);
      toast.success(lang === "zh" ? `欢迎，${s.name_en}` : `Welcome, ${s.name_en}`);
      navigate("/staff", { replace: true });
    } catch (err) {
      toast.error(getErrorDetail(err, lang === "zh" ? "用户名或密码错误" : "Invalid credentials"));
    } finally { setLoading(false); }
  }

  return (
    <main data-testid="staff-login-page" className="min-h-[calc(100vh-4rem)] grid grid-cols-1 md:grid-cols-12">
      <div className="md:col-span-7 relative hidden md:block">
        <img
          src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(var(--background))]/95 via-[hsl(var(--background))]/20 to-transparent" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="inline-flex items-center gap-1 text-xs tracking-[0.25em] uppercase text-[hsl(var(--foreground))]/70 hover:text-[hsl(var(--primary))]"><ArrowLeft className="w-3 h-3" />{lang === "zh" ? "返回首页" : "Back home"}</Link>
          <p className="font-display text-5xl tracking-tight font-medium max-w-md leading-tight">{lang === "zh" ? "为我们出色的老师与同事而建。" : "Built for our wonderful teachers & staff."}</p>
        </div>
      </div>
      <div className="md:col-span-5 flex items-center justify-center p-8 md:p-16 bg-[hsl(var(--background))]">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="staff-login-form">
          <div>
            <p className="text-xs tracking-[0.25em] text-[hsl(var(--muted-foreground))] flex items-center gap-2"><Briefcase className="w-3 h-3" />{lang === "zh" ? "教职员入口" : "DAWNVILLE — STAFF PORTAL"}</p>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight font-medium mt-3">{lang === "zh" ? "教职员登录" : "Staff sign-in"}</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2">{lang === "zh" ? "使用您的用户名或邮箱登录。" : "Sign in with your username or email."}</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="staff-identifier" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "用户名或邮箱" : "Username or email"}</Label>
              <Input id="staff-identifier" required value={identifier} onChange={(e)=>setIdentifier(e.target.value)} data-testid="staff-login-identifier" className="mt-2" />
            </div>
            <div>
              <Label htmlFor="staff-password" className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">{lang === "zh" ? "密码" : "Password"}</Label>
              <Input id="staff-password" type="password" required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="staff-login-password" className="mt-2" />
              <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))] italic">{lang === "zh" ? "首次登录密码：00000（建议立即更改）" : "First-time password: 00000 (please change after sign-in)."}</p>
            </div>
          </div>
          <Button type="submit" disabled={loading} data-testid="staff-login-submit" className="w-full bg-[hsl(var(--foreground))] hover:bg-[hsl(var(--foreground))]/90 text-[hsl(var(--background))] rounded-full">{loading ? "…" : (lang === "zh" ? "登录" : "Sign in")}</Button>
        </form>
      </div>
    </main>
  );
}
